import { BreadcrumbSchema, PageHead, WebPageSchema } from '@/shared/seo';
import { APP_IDS, SITE_URL } from '@/shared/site';
import { MS_THEME_COLOR } from '@/membersheep/config';
import { MS_PAGES, msCopyFor, msPath, useMsCopy, useMsLocale, type MsLocale, type MsPage } from '@/membersheep/i18n';

/** Each locale's tag, plus bare zh-Hant so Hong Kong and Macau readers land on the Chinese page too. English first (x-default). */
const ALTERNATE_LANGS: { locale: MsLocale; lang: string }[] = [
  { locale: 'en', lang: msCopyFor('en').htmlLang },
  { locale: 'zh-TW', lang: msCopyFor('zh-TW').htmlLang },
  { locale: 'zh-TW', lang: 'zh-Hant' }
];

interface MsHeadProps {
  page: MsPage;
  title: string;
  description: string;
}

/**
 * Head tags for a MemberSheep page, with hreflang to the same page in the other language, plus
 * its WebPage and breadcrumb JSON-LD (Travo Labs › MemberSheep › page).
 */
export const MsHead = ({ page, title, description }: MsHeadProps) => {
  const locale = useMsLocale();
  const copy = useMsCopy();
  const url = SITE_URL + msPath(locale, page);
  const pageName: Record<MsPage, string | null> = {
    [MS_PAGES.home]: null,
    [MS_PAGES.privacy]: copy.privacy.title,
    [MS_PAGES.terms]: copy.terms.title,
    [MS_PAGES.support]: copy.support.title
  };
  const crumbs = [
    { name: 'MemberSheep', url: SITE_URL + msPath(locale, MS_PAGES.home) },
    ...(pageName[page] ? [{ name: pageName[page], url }] : [])
  ];

  return (
    <>
      <PageHead
      title={title}
      description={description}
      path={msPath(locale, page)}
      alternates={ALTERNATE_LANGS.map(({ locale: target, lang }) => ({ lang, path: msPath(target, page) }))}
      siteName="MemberSheep"
      ogLocale={copy.ogLocale}
      image={SITE_URL + copy.seo.ogImage}
        imageAlt={copy.seo.ogImageAlt}
      themeColor={MS_THEME_COLOR}
      />
      <WebPageSchema name={title} description={description} url={url} inLanguage={copy.htmlLang} about={APP_IDS.memberSheep} />
      <BreadcrumbSchema items={crumbs} />
    </>
  );
};
